import React, { useState, useEffect } from 'react';
import Button from '../UI/button/Button';
import styles from './HotelReservationInputs.module.css';
import { Formik } from 'formik';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import DatePickerField from '../UI/datePickerField/DatePickerField';
import Input from '../UI/input/Input';
import {
  IOnSubmitInput,
  IPartnerReservationFormValues,
  IPartnerReservationApiFormValues
} from '../../interfaces';
import { paginationConstants } from '../../utils/constant';
import { getDateFormat, getDateTypeTobeSearchedForApi } from '../../utils/utils';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface IHotelReservationInputs {
  postPartnerReservationListHandler: (values: IPartnerReservationApiFormValues) => void;
}
const HotelReservationInputs = ({ postPartnerReservationListHandler }: IHotelReservationInputs) => {
  const { t } = useTranslation();
  const { hotelId } = useParams();
  const [searchParams] = useSearchParams();
  const [bookerReservationFromDate, setBookerReservationFromDate] = useState<Date | null>(null);
  const [bookerReservationToDate, setBookerReservationToDate] = useState<Date | null>(null);

  useEffect(() => {
    const fromDateStr = searchParams.get('bookerReservationFromDate');
    const toDateStr = searchParams.get('bookerReservationToDate');

    if (fromDateStr && fromDateStr !== '' && toDateStr && toDateStr !== '') {
      try {
        const fromDate = new Date(parseInt(fromDateStr, 10)); // Convert the string to a Date object
        const toDate = new Date(parseInt(toDateStr, 10)); // Convert the string to a Date object
        if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
          setBookerReservationFromDate(fromDate);
          setBookerReservationToDate(toDate);
        } else {
          throw new Error('Invalid date format');
        }
      } catch (error) {
        setBookerReservationFromDate(null);
        setBookerReservationToDate(null);
      }
    }
  }, [searchParams]);

  const onSubmit = (values: IPartnerReservationFormValues, { setSubmitting }: IOnSubmitInput) => {
    const partnerReservationApiFormValues = {
      reservationId: '',
      hotelId: hotelId ? hotelId.toString() : '',
      dateUntil: values.dateUntil ? getDateFormat(values.dateUntil as Date) : '',
      dateFrom: values.dateFrom ? getDateFormat(values.dateFrom as Date) : '',
      dateTypeToBeSearched: getDateTypeTobeSearchedForApi(values.dateOfSelector),
      bookerName: '',
      pageNumber: paginationConstants.pageNumber,
      sortField: paginationConstants.sortField,
      sortOrder: paginationConstants.sortOrder,
      pageLimit: paginationConstants.pageLimit
    };
    if (/^(?=(?:\D*\d){10}\D*$)[\d\s.]+$/i.test(values.reservationIdOrName.toString())) {
      partnerReservationApiFormValues.reservationId = values.reservationIdOrName;
    } else {
      partnerReservationApiFormValues.bookerName = values.reservationIdOrName;
    }
    postPartnerReservationListHandler(partnerReservationApiFormValues);
    setSubmitting(false);
  };
  const options = ['Check-in', 'Check-out'];

  const handleFromDateChange = (
    name: string,
    date: Date | null,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setBookerReservationFromDate(date);
    setFieldValue(name, date);
  };

  const handleToDateChange = (
    name: string,
    date: Date | null,
    setFieldValue: (field: string, value: any) => void
  ) => {
    setBookerReservationToDate(date);
    setFieldValue(name, date);
  };

  return (
    <>
      <Formik
        initialValues={{
          dateOfSelector: 'Check-in',
          dateFrom: new Date(),
          dateUntil: new Date(),
          reservationIdOrName: ''
        }}
        onSubmit={onSubmit}>
        {({ values, handleSubmit, handleChange, setFieldValue, isSubmitting }) => (
          <form onSubmit={handleSubmit} className={styles.hotelReservationInputsContainer}>
            <Box data-testid="basicSelectTestId">
              <FormControl className={styles.selectDateOuterContainer} style={{ width: '100%' }}>
                <label
                  htmlFor={'dateOfSelector'}
                  className={styles.selectDateLabelText}
                  id="dateOfSelector">
                  {t('Date-Of')}
                </label>
                <Select
                  sx={{
                    '.MuiOutlinedInput-notchedOutline': {
                      borderColor: '#949494'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0071c2'
                    }
                  }}
                  className={styles.selectDateContainer}
                  labelId="dateOfSelector"
                  name={'dateOfSelector'}
                  id="dateOfSelector"
                  data-testid="dateOfSelectorTestId"
                  value={values.dateOfSelector}
                  defaultValue={t(`${options[0]}`)}
                  onChange={(e) => {
                    setFieldValue('dateOfSelector', e.target.value);
                  }}>
                  {options.map((x) => (
                    <MenuItem key={x} value={x}>
                      {t(`${x}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <div className={styles.datePickerFieldContainer}>
              <DatePickerField
                dateFieldInputClassName={styles.dateFieldInputDiv}
                id={'dateFrom'}
                label={t('From')}
                name={'dateFrom'}
                onChange={(name, date) => handleFromDateChange(name, date, setFieldValue)}
                value={bookerReservationFromDate ? bookerReservationFromDate : values.dateFrom}
                maxDate={values.dateUntil && new Date(values.dateUntil)}
              />
              <DatePickerField
                dateFieldInputClassName={styles.dateFieldInputDiv}
                id={'dateUntil'}
                label={t('Until')}
                name={'dateUntil'}
                onChange={(name, date) => handleToDateChange(name, date, setFieldValue)}
                value={bookerReservationToDate ? bookerReservationToDate : values.dateUntil}
                minDate={values.dateFrom && new Date(values.dateFrom)}
              />
            </div>
            <Input
              inputClassName={styles.searchInput}
              data-testid={'SearchId'}
              label={`${t('Search')}`}
              id="reservationIdOrName"
              name="reservationIdOrName"
              type={'text'}
              placeholder={`${t('ReservationIdOrTravelerName')}`}
              onChange={handleChange}
              onBlur={handleChange}
              value={values.reservationIdOrName}
            />
            <Button
              className={styles.searchReservationButton}
              type={'submit'}
              disabled={isSubmitting}>
              {t('partner_show_reservation')}
            </Button>
          </form>
        )}
      </Formik>
    </>
  );
};

export default HotelReservationInputs;
